/* NEUTRON SPORTS & OUTDOORS — custom behaviour for NX sections */
(() => {
  /* ---------- Toast ---------- */
  let toastEl;
  let toastTimer;
  function toast(msg, isError) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'nx-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = msg;
    toastEl.classList.toggle('nx-toast--error', !!isError);
    toastEl.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toastEl.classList.remove('is-visible'), 2800);
  }

  /* ---------- Hero slider ---------- */
  if (!customElements.get('nx-slider')) {
    customElements.define('nx-slider', class extends HTMLElement {
      connectedCallback() {
        this.slides = [...this.querySelectorAll('.nx-hero__slide')];
        this.dots = [...this.querySelectorAll('[data-nx-dot]')];
        this.index = 0;
        if (this.slides.length < 2) return;
        this.querySelector('[data-nx-prev]')?.addEventListener('click', () => { this.go(this.index - 1); this.restart(); });
        this.querySelector('[data-nx-next]')?.addEventListener('click', () => { this.go(this.index + 1); this.restart(); });
        this.dots.forEach((d) => d.addEventListener('click', () => { this.go(+d.dataset.nxDot); this.restart(); }));
        this.addEventListener('mouseenter', () => this.stop());
        this.addEventListener('mouseleave', () => this.start());
        let x0 = null;
        this.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; }, { passive: true });
        this.addEventListener('touchend', (e) => {
          if (x0 === null) return;
          const dx = e.changedTouches[0].clientX - x0;
          if (Math.abs(dx) > 40) { this.go(this.index + (dx < 0 ? 1 : -1)); this.restart(); }
          x0 = null;
        });
        document.addEventListener('shopify:block:select', (e) => {
          const i = this.slides.indexOf(e.target);
          if (i > -1) { this.stop(); this.go(i); }
        });
        this.start();
      }
      go(i) {
        const n = this.slides.length;
        this.index = (i + n) % n;
        this.slides.forEach((s, k) => s.classList.toggle('is-active', k === this.index));
        this.dots.forEach((d, k) => d.classList.toggle('is-active', k === this.index));
      }
      start() {
        if (this.dataset.autoplay !== 'true' || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        this.stop();
        this.timer = setInterval(() => this.go(this.index + 1), +this.dataset.speed || 6000);
      }
      stop() { clearInterval(this.timer); }
      restart() { this.stop(); this.start(); }
      disconnectedCallback() { this.stop(); }
    });
  }

  /* ---------- Tabs ---------- */
  if (!customElements.get('nx-tabs')) {
    customElements.define('nx-tabs', class extends HTMLElement {
      connectedCallback() {
        this.btns = [...this.querySelectorAll('[data-nx-tab]')];
        this.panels = [...this.querySelectorAll('.nx-tabs__panel')];
        this.btns.forEach((b) => b.addEventListener('click', () => this.show(+b.dataset.nxTab)));
        document.addEventListener('shopify:block:select', (e) => {
          const i = this.btns.indexOf(e.target);
          if (i > -1) this.show(i);
        });
      }
      show(i) {
        this.btns.forEach((b, k) => {
          b.classList.toggle('is-active', k === i);
          b.setAttribute('aria-selected', k === i ? 'true' : 'false');
        });
        this.panels.forEach((p, k) => {
          p.hidden = k !== i;
          p.classList.toggle('is-active', k === i);
        });
      }
    });
  }

  /* ---------- Countdown ---------- */
  if (!customElements.get('nx-countdown')) {
    customElements.define('nx-countdown', class extends HTMLElement {
      connectedCallback() {
        this.end = new Date(this.dataset.end).getTime();
        if (Number.isNaN(this.end)) { this.hidden = true; return; }
        this.tick();
        this.timer = setInterval(() => this.tick(), 1000);
      }
      tick() {
        let s = Math.max(0, Math.floor((this.end - Date.now()) / 1000));
        const d = Math.floor(s / 86400); s -= d * 86400;
        const h = Math.floor(s / 3600); s -= h * 3600;
        const m = Math.floor(s / 60); s -= m * 60;
        const pad = (v) => String(v).padStart(2, '0');
        this.querySelector('[data-d]').textContent = pad(d);
        this.querySelector('[data-h]').textContent = pad(h);
        this.querySelector('[data-m]').textContent = pad(m);
        this.querySelector('[data-s]').textContent = pad(s);
        if (this.end <= Date.now()) clearInterval(this.timer);
      }
      disconnectedCallback() { clearInterval(this.timer); }
    });
  }

  /* ---------- Quick add to cart (NX product cards) ---------- */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-nx-add]');
    if (!btn) return;
    e.preventDefault();
    const label = btn.querySelector('span');
    const original = label ? label.textContent : '';
    btn.classList.add('is-loading');
    try {
      const routes = (window.theme && window.theme.routes) || {};
      const res = await fetch(`${routes.cartAdd || '/cart/add'}.js`.replace('.js.js', '.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: +btn.dataset.nxAdd, quantity: 1 }], sections: 'cart-icon-bubble' })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || data.message || 'Could not add to cart');

      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble && data.sections && data.sections['cart-icon-bubble']) {
        bubble.innerHTML = data.sections['cart-icon-bubble'];
      }
      btn.classList.add('is-added');
      if (label) label.textContent = 'Added!';
      setTimeout(() => { btn.classList.remove('is-added'); if (label) label.textContent = original; }, 2000);

      const afterAtc = window.theme && window.theme.settings && window.theme.settings.afterAtc;
      if (afterAtc === 'page') {
        window.location.href = routes.cart || '/cart';
      } else if (document.querySelector('cart-drawer')) {
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
        if (afterAtc === 'drawer') {
          setTimeout(() => document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', { bubbles: true, detail: { opener: btn } })), 450);
        } else {
          toast('Added to cart');
        }
      } else {
        toast('Added to cart');
      }
      btn.dispatchEvent(new CustomEvent('on:cart:add', { bubbles: true, detail: { variantId: +btn.dataset.nxAdd } }));
    } catch (err) {
      toast(err.message || 'Could not add to cart', true);
    } finally {
      btn.classList.remove('is-loading');
    }
  });

  /* ---------- Recently viewed ---------- */
  const RV_KEY = 'nx_recently_viewed';
  const rvRead = () => { try { return JSON.parse(localStorage.getItem(RV_KEY)) || []; } catch (e) { return []; } };
  const rvWrite = (list) => { try { localStorage.setItem(RV_KEY, JSON.stringify(list)); } catch (e) { /* storage unavailable */ } };
  if (window.nxProductHandle) {
    const list = rvRead().filter((h) => h !== window.nxProductHandle);
    list.unshift(window.nxProductHandle);
    rvWrite(list.slice(0, 16));
  }

  const formatMoney = (cents, format) => {
    const value = (cents / 100).toFixed(2);
    const withCommas = value.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return (format || '${{amount}}')
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, withCommas.split('.')[0])
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, value.replace('.', ','))
      .replace(/\{\{\s*amount\s*\}\}/, withCommas);
  };
  const esc = (s) => String(s || '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  if (!customElements.get('nx-recently-viewed')) {
    customElements.define('nx-recently-viewed', class extends HTMLElement {
      async connectedCallback() {
        const limit = +this.dataset.limit || 5;
        const handles = rvRead().filter((h) => h !== this.dataset.current).slice(0, limit);
        if (!handles.length) return;
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const results = await Promise.all(handles.map((h) => fetch(`${root}products/${h}.js`).then((r) => (r.ok ? r.json() : null)).catch(() => null)));
        const items = results.filter(Boolean);
        if (!items.length) return;
        const money = this.dataset.money;
        this.querySelector('ul').innerHTML = items.map((p) => {
          const img = p.featured_image ? `${p.featured_image}${p.featured_image.includes('?') ? '&' : '?'}width=400` : '';
          const sale = p.compare_at_price > p.price;
          const variant = (p.variants || []).find((v) => v.available) || p.variants[0];
          const single = p.variants.length === 1;
          let action = `<a href="${p.url}" class="nx-btn nx-btn--outline nx-btn--block">View details</a>`;
          if (p.available && single) action = `<button type="button" class="nx-btn nx-btn--accent nx-btn--block" data-nx-add="${variant.id}"><span>Add to cart</span></button>`;
          else if (p.available) action = `<a href="${p.url}" class="nx-btn nx-btn--accent nx-btn--block">Choose options</a>`;
          return `<li><article class="nx-card${p.available ? '' : ' nx-card--soldout'}">
            <a href="${p.url}" class="nx-card__media">${img ? `<img class="nx-card__img" src="${img}" alt="${esc(p.title)}" loading="lazy" width="400" height="400">` : ''}
              <span class="nx-card__badges">${p.available ? '' : '<span class="nx-badge nx-badge--muted">Sold out</span>'}</span></a>
            <div class="nx-card__body">
              <p class="nx-card__vendor">${esc(p.vendor)}</p>
              <h3 class="nx-card__title"><a href="${p.url}">${esc(p.title)}</a></h3>
              <div class="nx-card__price"><span class="nx-price${sale ? ' nx-price--sale' : ''}">${formatMoney(p.price, money)}</span>${sale ? `<s class="nx-price--compare">${formatMoney(p.compare_at_price, money)}</s>` : ''}</div>
              <div class="nx-card__actions">${action}</div>
            </div></article></li>`;
        }).join('');
        const section = this.closest('.nx-recent');
        if (section) section.hidden = false;
      }
    });
  }
})();
